/// <reference path="../../../../typings/index.d.ts" />
/// <reference path="../../components/models/meevo.model.custom.ts" />
/// <reference path="../reports/reports.models.ts" />
/// <reference path="../reports/reports.helper.ts" />

module Meevo.Plugins.PerformanceInsights {

    import IReportGeneralInfo = Meevo.Model.IReportGeneral;
    import IReportCategory = Meevo.Plugins.Reports.IReportCategory;
    import IReportsHelper = Meevo.Plugins.Reports.IReportsHelper;
    import SecurityAccessEnum = Meevo.Model.SecurityAccessEnum;
    import ObjectStateEnum = Meevo.Model.ObjectStateEnum;
    import PageStateEnum = Meevo.Model.PageStateEnum;
    import ReportAvailabilityEnum = Meevo.Model.ReportAvailabilityEnum;
    import SecurityParts = Meevo.Components.Common.SecurityParts;
    import SecurityKeys = Meevo.Components.Common.SecurityKeys;
    import ReportRenderingEnum = Meevo.Model.ReportRenderingEnum;

    const ALL_CATEGORY: IReportCategory = {
        displayName: 'All Dashboards',
        desc: 'All Dashboards',
        id: null,
    };

    const MIN_LENGTH_FOR_SEARCH = 3;
    type QsDashboardAccessState = 'idle' | 'checking' | 'pending' | 'allowed' | 'unavailable';

    export interface IPerformanceInsightsContext {
        reportCategories: Array<IReportCategory>;
        selectedCategory: IReportCategory;
        reportList: Array<IReportGeneralInfo>;
        selectedReport: IReportGeneralInfo;
        showMenuType: Number;
        searchFilter: string;

        goBack(): void;

        activate(): void;

        loadReportCategories(showBusy?: boolean): ng.IPromise<IReportCategory[]>;

        loadReportListByCategoryId(showBusy?: boolean): ng.IPromise<Array<IReportGeneralInfo>>;

        loadReportInputTemplate(report: IReportGeneralInfo, showBusy?: boolean): ng.IPromise<any>;

        loadReportFromParams(params: any): void;

        loadReportCategoryFromParams(params: any): void;
    }

    export class PerformanceInsightsContext implements IPerformanceInsightsContext {
        reportList: Array<IReportGeneralInfo>;
        selectedReport: IReportGeneralInfo;
        reportCategories: Array<IReportCategory>;
        selectedCategory: IReportCategory;
        qsSessionStarted: boolean = false;
        /** True only after the viewer has successfully created a dashboard session. */
        qsViewerHasActiveSession: boolean = false;
        qsDashboardAccessState: QsDashboardAccessState = 'idle';
        qsDashboardAccessError: string = '';
        private qsAccessCheckVersion: number = 0;
        showMenuType: PageStateEnum;
        isBackArrowVisible: boolean;
        searchFilter: string;
        isEmptyReportList: boolean = false;

        get searchBtnDisabled(): boolean {
            return !this.searchFilter || this.searchFilter.length < MIN_LENGTH_FOR_SEARCH;
        }

        get isQuickSightReportLoaded(): boolean {
            return this.showMenuType === PageStateEnum.Report && !!this.selectedReport && this.selectedReport.renderingEnum === ReportRenderingEnum.QuickSight;
        }

        /** All QuickSight dashboard IDs from the current report list — used to authorize the embed session for free cross-dashboard navigation */
        get qsDashboardIds(): number[] {
            if (!this.reportList) return [];
            return this.reportList
                .filter(r => r.renderingEnum === ReportRenderingEnum.QuickSight)
                .map(r => r.id);
        }

        get qsDashboardAccessAllowed(): boolean {
            return this.qsDashboardAccessState === 'allowed';
        }

        get qsDashboardAccessChecking(): boolean {
            return this.qsDashboardAccessState === 'checking';
        }

        get qsDashboardAccessPending(): boolean {
            return this.qsDashboardAccessState === 'pending';
        }

        get qsDashboardAccessUnavailable(): boolean {
            return this.qsDashboardAccessState === 'unavailable';
        }

        constructor(protected appSettings: Meevo.Components.Common.IAppSettings,
            protected dataService: Meevo.Components.BaseClasses.IDataService,
            protected modalService: Meevo.Components.Modal.IModalService,
            protected identityService: Meevo.Components.Common.IIdentityService,
            protected loggingService: Meevo.Components.Logging.ILoggingService,
            protected busyService: Meevo.Components.Busy.IBusyService,
            protected securityService: Meevo.Components.Common.ISecurityService,
            protected $scope: ng.IScope,
            protected $q: angular.IQService,
            protected $http: angular.IHttpService,
            protected reportsHelper: IReportsHelper,
            protected $translate: angular.translate.ITranslateService) {
            this.showMenuType = PageStateEnum.CategoryList;
            $scope.$on('performanceInsights:qs-ready', () => {
                this.qsViewerHasActiveSession = true;
            });
        }

        /**
         * Initial function of context
         */
        activate(): void {
            this.loadReportCategories()
                .then(() => {
                    this.selectedCategory = this.reportCategories[0];
                    this.loadReportListByCategoryId();
                });
        }

        /**
         * Load list of report categories
         */
        loadReportCategories(showBusy: boolean = true): ng.IPromise<IReportCategory[]> {
            if (showBusy) {
                this.busyService.start();
            }

            return this.dataService.getListPaged(this.appSettings.API_ENDPOINTS.reportCategories, {
                criteria: {
                    isContainsReports: true,
                    objectStateEnum: ObjectStateEnum.Active
                }
            })
                .then((result: IReportCategory[]) => this.checkSecurityReport(result))
                .then((result: IReportCategory[]) => {
                    this.reportCategories = [{ ...ALL_CATEGORY }, ...result];
                    return result;
                })
                .finally(() => {
                    if (showBusy) {
                        this.busyService.stop();
                    }
                });
        }

        /**
         * Check categories by security key (Performance Insights namespace).
         * Categories whose INSIGHTS:<category>:READ key is undefined in the backend security store
         * (checkSecurityKey rejects with null for unknown keys) are treated as inaccessible and
         * filtered out — this prevents one missing key from failing the whole batch.
         */
        checkSecurityReport(reports: IReportCategory[]): ng.IPromise<IReportCategory[]> {
            let promises = reports.map((report) => {
                const securityName: string = this.reportsHelper.reportNameToSecurityKey(report.displayName);
                return this.securityService
                    .checkSecurityKey('INSIGHTS:' + securityName + SecurityParts.KEY_PART_READ)
                    .catch(() => SecurityAccessEnum.Hide);
            });

            return this.$q.all(promises).then(() => {
                let result: IReportCategory[] = [];
                reports.forEach((item) => {
                    const securityName: string = this.reportsHelper.reportNameToSecurityKey(item.displayName);
                    if (this.securityService.hasSecurity('INSIGHTS:' + securityName + SecurityParts.KEY_PART_READ)) {
                        result.push(item);
                    }
                });
                return result;
            });
        }

        /**
         * Load list of QuickSight dashboards for the selected category
         */
        loadReportListByCategoryId(showBusy: boolean = true, isReportWithParams: boolean = false): ng.IPromise<Array<IReportGeneralInfo>> {
            if (showBusy) {
                this.busyService.start();
            }

            if (!isReportWithParams) {
                this.setAnalyticsTrackingUrl(this.selectedCategory.displayName, undefined);
            }

            return this.dataService.getListPaged(this.appSettings.API_ENDPOINTS.report, {
                criteria: {
                    filter: this.searchFilter || '',
                    categoryId: this.selectedCategory.id,
                    objectStateEnum: ObjectStateEnum.Active
                }
            })
                .then((result: Array<IReportGeneralInfo> | any) => {
                    let reports: Array<IReportGeneralInfo> = [];

                    this.isEmptyReportList = result.length === 0;

                    let promises = result
                        .filter((report) => report.renderingEnum === ReportRenderingEnum.QuickSight)
                        .filter((report) => {
                            if (report.reportAvailability === ReportAvailabilityEnum.Both) {
                                return true;
                            }
                            if (this.identityService.inCentralOfficeMode()) {
                                return report.reportAvailability === ReportAvailabilityEnum.GlobalCentralOffice;
                            } else {
                                return report.reportAvailability === ReportAvailabilityEnum.Location;
                            }
                        })
                        .map((report) => {
                            // Translate dashboard display name if applicable
                            var translatedDisplayName = this.$translate.instant(`LABEL_${report.shortCut.toUpperCase()}_DISPLAY_NAME`);
                            if (translatedDisplayName && translatedDisplayName.indexOf('LABEL_') === -1) {
                                report.displayName = translatedDisplayName;
                            }
                            // Translate dashboard description if applicable
                            var translatedDescription = this.$translate.instant(`LABEL_${report.shortCut.toUpperCase()}_DESCRIPTION`);
                            if (translatedDescription && translatedDescription.indexOf('LABEL_') === -1) {
                                report.description = translatedDescription;
                            }
                            return report;
                        })
                        .map((report) => this.securityService.checkSecurityKey(report.securityKey)
                            .then((permission: SecurityAccessEnum) => {
                                report.permission = permission;
                                if (permission !== SecurityAccessEnum.Hide) {
                                    reports.push(report);
                                }
                            })
                            // Defensive: unknown security keys reject from checkSecurityKey.
                            // Treat as hidden so a single missing key doesn't fail the whole batch.
                            .catch(() => { /* report filtered out */ })
                        );

                    return this.$q.all(promises).then(() => {
                        this.reportList = reports;
                        this.showMenuType = PageStateEnum.ReportList;
                        this.isBackArrowVisible = this.checkPageStatus();
                        return result;
                    });
                })
                .finally(() => {
                    if (showBusy) {
                        this.busyService.stop();
                    }
                });
        }

        /**
         * Open selected QuickSight dashboard
         */
        loadReportInputTemplate(report: IReportGeneralInfo, showBusy: boolean = true): ng.IPromise<any> {
            this.selectedReport = report;

            this.showMenuType = PageStateEnum.Report;
            this.isBackArrowVisible = this.checkPageStatus();
            this.setAnalyticsTrackingUrl(this.selectedCategory.displayName, report.shortCut);

            if (this.qsViewerHasActiveSession || this.qsDashboardAccessAllowed) {
                this.qsSessionStarted = true;
                return this.$q.resolve();
            }

            return this.checkQsDashboardAccess()
                .then((allowed: boolean) => {
                    if (allowed) {
                        this.qsSessionStarted = true;
                    }
                });
        }

        private checkQsDashboardAccess(): ng.IPromise<boolean> {
            var version = ++this.qsAccessCheckVersion;
            var identity = this.identityService.current();

            if (identity.userTypeEnum === Meevo.Model.UserTypeEnum.MSIUser) {
                this.qsDashboardAccessState = 'allowed';
                this.qsDashboardAccessError = '';
                return this.$q.resolve(true);
            }

            var userId = String(this.identityService.loggedInUserId() || '');
            if (!userId) {
                this.qsDashboardAccessState = 'unavailable';
                this.qsDashboardAccessError = 'Unable to verify dashboard registration. Please try again.';
                return this.$q.resolve(false);
            }

            this.qsDashboardAccessState = 'checking';
            this.qsDashboardAccessError = '';

            return this.$http.get<{ registered?: any }>(
                `/${this.appSettings.API_ROOT}/Report/QuickSightViewer/Registration/${encodeURIComponent(userId)}`,
            ).then((response) => {
                if (version !== this.qsAccessCheckVersion) {
                    return false;
                }
                if (!response.data || typeof response.data.registered !== 'boolean') {
                    this.qsDashboardAccessState = 'unavailable';
                    this.qsDashboardAccessError = 'Unable to verify dashboard registration. Please try again.';
                    return false;
                }
                if (response.data.registered) {
                    this.qsDashboardAccessState = 'allowed';
                    return true;
                }
                this.qsDashboardAccessState = 'pending';
                return false;
            }, () => {
                if (version === this.qsAccessCheckVersion) {
                    this.qsDashboardAccessState = 'unavailable';
                    this.qsDashboardAccessError = 'Unable to verify dashboard registration. Please try again.';
                }
                return false;
            });
        }

        loadReportFromParams(params: any): void {
            if (
                this.showMenuType === PageStateEnum.Report &&
                this.selectedReport &&
                this.selectedReport.id === params.reportId
            ) {
                // QuickSight dashboard already loaded, pass
                return;
            }

            this.busyService.start();
            this.loadReportCategories(false).then(() => {
                // Try exact category first; fall back to 'All Dashboards' so a
                // category rename or security-key mismatch doesn't silently block
                // convo-driven navigation.
                this.selectedCategory = _.findWhere(this.reportCategories, { id: params.reportContext.reportCatId })
                    || this.reportCategories[0];
                this.loadReportListByCategoryId(false, true).then(() => {
                    this.selectedReport = _.findWhere(this.reportList, { id: params.reportContext.id });
                    if (this.selectedReport) {
                        if (this.selectedReport.permission === SecurityAccessEnum.Deny) {
                            this.busyService.stop();
                            this.modalService.warning('LABEL_REPORT_ACCESS_DENIED', Components.Modal.ModalTranslationModes.Translate);
                            return;
                        }
                        this.loadReportInputTemplate(this.selectedReport, false)
                            .then(() => {
                                this.busyService.stop();
                            });
                    } else {
                        this.busyService.stop();
                    }
                });
            });
        }

        loadReportCategoryFromParams(params: any): void {
            this.busyService.start();
            this.loadReportCategories(false)
                .then(() => {
                    this.selectedCategory = _.findWhere(this.reportCategories, { id: params.reportContext.reportCatId })
                        || this.reportCategories[0];
                    return this.loadReportListByCategoryId(false);
                })
                .then(() => this.busyService.stop());
        }

        /**
         * Navigate to previous screen
         */
        goBack() {
            if (this.showMenuType === PageStateEnum.Report) {
                this.showMenuType = PageStateEnum.ReportList;
                // A pending/unavailable viewer has no paid session to preserve.
                // Let ng-if destroy it so its access-state layout cannot flash
                // in a collapsed host container when navigating back.
                if (!this.qsViewerHasActiveSession) {
                    this.qsAccessCheckVersion++;
                    this.qsSessionStarted = false;
                    this.qsDashboardAccessState = 'idle';
                    this.qsDashboardAccessError = '';
                }
                this.isBackArrowVisible = this.checkPageStatus();
                this.setAnalyticsTrackingUrl(this.selectedCategory.displayName, undefined);
                return;
            }

            switch (this.showMenuType) {
                case PageStateEnum.CategoryList:
                    break;
                case PageStateEnum.ReportList:
                    this.showMenuType = PageStateEnum.CategoryList;
                    this.setAnalyticsTrackingUrl(undefined, undefined);
                    break;
                default:
                    break;
            }
            this.isBackArrowVisible = this.checkPageStatus();
        }

        checkPageStatus(): boolean {
            return this.showMenuType !== PageStateEnum.CategoryList && this.showMenuType !== PageStateEnum.ReportList;
        }

        get pageState(): any {
            return PageStateEnum;
        }

        search(event?: KeyboardEvent) {
            if (event) {
                event.preventDefault();
            }

            if (this.searchBtnDisabled) {
                return;
            }

            const allCategory = this.reportCategories.find((category) => category.id === ALL_CATEGORY.id);
            if (!!allCategory) {
                this.selectedCategory = allCategory;
            }

            this.loadReportListByCategoryId(true);
        }

        private setAnalyticsTrackingUrl(category: string, shortCut: string) {
            const pendo = window['pendo'];
            if (!pendo || !pendo.location) return;

            let url = `${window.location.origin}/performanceInsights`;
            if (category) url += `/${category.replace(/[^a-zA-Z]+/g, '')}`;
            if (shortCut) url += `/${shortCut}`;
            pendo.location.setUrl(url);
            console.log(`PENDO: ${url}`);
        }
    }
}
